import * as Sentry from "@sentry/nextjs";

import { GIT_COMMIT_SHA, IS_SERVER, SENTRY_DSN, SENTRY_SERVER_ROOT_DIR } from "../shared/constants";
import { IGNORE_ERRORS } from "../shared/ignore-errors.constants";
import { isInjectedThirdPartyError } from "./sentry-filters";

// URL patterns to ignore
const IGNORED_URL_PATTERNS = ["/api/health", "/api/status", "/builder-preview/"];

let initialized = false;

export const init = (): void => {
    if (SENTRY_DSN && !initialized) {
        const integrations = [];
        if (IS_SERVER && SENTRY_SERVER_ROOT_DIR) {
            // For Node.js, rewrite Error.stack to use relative paths, so that source
            // maps starting with ~/_next map to files in Error.stack with path
            // app:///_next
            integrations.push(
                Sentry.rewriteFramesIntegration({
                    iteratee: (frame) => {
                        if (frame.filename) {
                            frame.filename = frame.filename.replace(
                                SENTRY_SERVER_ROOT_DIR,
                                "app:///"
                            );
                            frame.filename = frame.filename.replace(".next", "_next");
                        }
                        return frame;
                    },
                })
            );
        }

        Sentry.init({
            enabled: !!SENTRY_DSN,
            integrations,
            dsn: SENTRY_DSN,
            release: GIT_COMMIT_SHA,
            ignoreErrors: IGNORE_ERRORS,
            beforeSend: (event) => {
                // Check if the error is related to a URL we want to ignore
                const url = event.request?.url || event.transaction || "";

                // Check against ignored URL patterns
                const shouldIgnore = IGNORED_URL_PATTERNS.some((pattern) => url.includes(pattern));

                if (shouldIgnore) {
                    return null; // Don't send the event
                }

                // Errors thrown by scripts injected into the page by something we don't ship.
                // Browser events only — server stacks don't use the app:/// frame paths this
                // relies on, so applying it there would drop legitimate Node exceptions.
                if (!IS_SERVER && isInjectedThirdPartyError(event)) {
                    return null; // Don't send the event
                }

                return event; // Send the event
            },
        });
        initialized = true;
    }
};

init();

export default Sentry;
